import { Box, Link, Paper, Typography } from '@mui/material';
import { PageContainer } from '@toolpad/core/PageContainer';
import type React from 'react';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <PageContainer title="Privacy Policy">
      <Paper sx={{ p: 3, mt: 0 }}>
        <Typography paragraph>
          BIDLOGIQ is committed to providing quality services to you and this policy outlines our
          ongoing obligations to you in respect of how we manage your Personal Information.
        </Typography>
        <Typography paragraph>
          We have adopted the Australian Privacy Principles (APPs) contained in the Privacy Act 1988
          (Cth) (the Privacy Act). The APPs govern the way in which we collect, use, disclose,
          store, secure and dispose of your Personal Information.
        </Typography>
        <Typography paragraph>
          A copy of the Australian Privacy Principles may be obtained from the website of The Office
          of the Australian Information Commissioner at{' '}
          <Link href="https://www.oaic.gov.au/" target="_blank" rel="noopener">
            https://www.oaic.gov.au/
          </Link>
          .
        </Typography>

        <Box sx={{ my: 4 }}>
          <Typography variant="h5" gutterBottom>
            What is Personal Information and why do we collect it?
          </Typography>
          <Typography paragraph>
            Personal Information is information or an opinion that identifies an individual.
            Examples of Personal Information we collect include names, addresses, email addresses,
            and phone numbers.
          </Typography>
          <Typography paragraph>
            This Personal Information is obtained in many ways including correspondence, by
            telephone, by email, via our website, from other publicly available sources, and from
            cookies.
          </Typography>
          <Typography paragraph>
            We collect your Personal Information for the primary purpose of providing our services
            to you.
          </Typography>
          <Typography paragraph>
            When we collect Personal Information we will, where appropriate and where possible,
            explain to you why we are collecting the information and how we plan to use it.
          </Typography>
        </Box>

        <Box sx={{ my: 4 }}>
          <Typography variant="h5" gutterBottom>
            Sensitive Information
          </Typography>
          <Typography paragraph>
            Sensitive information is defined in the Privacy Act to include information or opinion
            about such things as an individual's racial or ethnic origin, political opinions,
            membership of a political association, religious or philosophical beliefs, membership of
            a trade union or other professional body, criminal record or health information.
          </Typography>
          <Typography paragraph>No sensitive information will be used by BIDLOGIQ.</Typography>
        </Box>

        <Box sx={{ my: 4 }}>
          <Typography variant="h5" gutterBottom>
            Third-Party Services and Data Processing
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Google Firebase
          </Typography>
          <Typography paragraph>
            We use several services from Google Firebase to provide the core functionality of our
            application. For these services, Google acts as a data processor on our behalf. The
            primary Firebase services we use are:
          </Typography>
          <ul>
            <li>Cloud Firestore (Database)</li>
            <li>Cloud Functions for Firebase (Backend-as-a-service)</li>
            <li>Cloud Storage for Firebase (File storage)</li>
            <li>Firebase Authentication (User management)</li>
            <li>Firebase Hosting (Web application hosting)</li>
            <li>Firebase A/B Testing (Feature testing)</li>
          </ul>
          <Typography paragraph>
            These services are certified under major privacy and security standards, including ISO
            27001 and SOC 1/2/3. Data you provide is encrypted in transit (using HTTPS) and at rest
            by these services. For more details, you can refer to the{' '}
            <Link
              href="https://firebase.google.com/support/privacy/"
              target="_blank"
              rel="noopener"
            >
              Privacy and Security in Firebase
            </Link>{' '}
            documentation.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Google Gemini API & Enterprise AI Governance
          </Typography>
          <Typography paragraph>
            While all of your stored databases (Cloud Firestore) and encrypted document files (Cloud
            Storage) are hosted at rest in your designated regional deployment (Sydney, Australia{' '}
            <code>australia-southeast1</code>; London, United Kingdom <code>europe-west2</code>; or
            Iowa, United States <code>us-central1</code>), our AI-powered features—such as capability
            report generation, compliance alignment, draft responses, and reviews—are processed
            transiently via Google&apos;s global Paid-Tier Gemini API and Gemini Enterprise Agent
            Platform endpoints. To provide these services, relevant data from your tenders and
            documents is decrypted transiently in volatile server memory and sent over encrypted TLS
            1.3 connections to the Gemini API for processing.
          </Typography>
          <Typography paragraph>
            Under our Paid-Tier / Enterprise API agreements, the{' '}
            <Link
              href="https://ai.google.dev/gemini-api/terms#paid-services"
              target="_blank"
              rel="noopener"
            >
              Gemini API Paid Services Terms
            </Link>
            , the{' '}
            <Link
              href="https://business.safety.google/processorterms/"
              target="_blank"
              rel="noopener"
            >
              Google Data Processor Addendum
            </Link>
            , and the{' '}
            <Link
              href="https://cloud.google.com/terms/service-terms"
              target="_blank"
              rel="noopener"
            >
              Google Cloud Service Specific Terms (§§ 17, 18, 20)
            </Link>
            , Google never uses your data, prompts, or generated outputs to train or fine-tune any
            AI/ML models, human reviewers never read your prompts, and your prompts are not retained
            outside your environment after request fulfillment. In alignment with the{' '}
            <Link
              href="https://cloud.google.com/terms/agentic-ai-use-guidelines"
              target="_blank"
              rel="noopener"
            >
              Google Cloud Agentic AI Responsible Use Guidelines
            </Link>
            , all AI agents operate strictly within your isolated tenant boundary under mandatory
            Human-in-the-Loop (HITL) review—never autonomously submitting tenders or modifying
            external records without your explicit confirmation.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Microsoft SharePoint Integration
          </Typography>
          <Typography paragraph>
            For users with Microsoft SharePoint integration, BIDLOGIQ provides the ability to access
            and process your company documents stored directly in your SharePoint environment.
          </Typography>
          <Typography paragraph>Specifically:</Typography>
          <ul>
            <li>
              <strong>Company Documents:</strong> Your source documents and files remain within your
              SharePoint tenant, governed by your existing security and access policies. BIDLOGIQ
              reads these files for processing and generative services, but does not store a
              separate copy on our systems.
            </li>
            <li>
              <strong>BIDLOGIQ Files and Metadata:</strong> Any files generated by BIDLOGIQ (e.g.,
              capability reports, draft responses) are also stored in your secure SharePoint tenant;
              however metadata remains in our secure Firebase environment.
            </li>
          </ul>
          <Typography paragraph>
            This separation ensures that you retain control over your primary documents while
            leveraging our platform's capabilities.
          </Typography>
        </Box>

        <Box sx={{ my: 4 }}>
          <Typography variant="h5" gutterBottom>
            Disclosure of Personal Information
          </Typography>
          <Typography paragraph>
            Your Personal Information may be disclosed in a number of circumstances including the
            following:
          </Typography>
          <ul>
            <li>Third parties where you consent to the use or disclosure; and</li>
            <li>Where required or authorised by law.</li>
          </ul>
        </Box>

        <Box sx={{ my: 4 }}>
          <Typography variant="h5" gutterBottom>
            Security and Retention of Personal Information
          </Typography>
          <Typography paragraph>
            Your Personal Information is stored in a manner that reasonably protects it from misuse
            and loss and from unauthorized access, modification, or disclosure.
          </Typography>
          <Typography paragraph>
            Data generated and stored by BIDLOGIQ within Google Cloud Storage and Cloud Firestore is
            encrypted both in transit (TLS 1.3) and at rest using Google Cloud Key Management
            Service (KMS) Application-Level Envelope Encryption (unique AES-256 Data Encryption Keys
            per file wrapped by a master KMS Key Encryption Key) and Transparent Data Encryption
            (TDE). For users with SharePoint integration, source documents remain in your SharePoint
            environment and are subject to your organization&apos;s security policies.
          </Typography>
          <Typography paragraph>
            When your Personal Information is no longer needed for the purpose for which it was
            obtained, we will take reasonable steps to destroy or permanently de-identify it.
            Archived tender files, including uploaded documents, responses, reports, and reviews,
            are automatically purged from storage 30 days after the tender is archived. You are
            encouraged to back up your important files to your own systems outside of the BIDLOGIQ
            platform, including your own SharePoint environment, as needed.
          </Typography>
          <Typography paragraph>
            However, most of the Personal Information related to your account and client file will
            be kept by us for a minimum of 7 years for legal and business record-keeping purposes.
          </Typography>
        </Box>

        <Box sx={{ my: 4 }}>
          <Typography variant="h5" gutterBottom>
            Access to your Personal Information
          </Typography>
          <Typography paragraph>
            You may access the Personal Information we hold about you and to update and/or correct
            it, subject to certain exceptions. If you wish to access your Personal Information,
            please contact us in writing.
          </Typography>
          <Typography paragraph>
            BIDLOGIQ will not charge any fee for your access request, but may charge an
            administrative fee for providing a copy of your Personal Information.
          </Typography>
          <Typography paragraph>
            In order to protect your Personal Information we may require identification from you
            before releasing the requested information.
          </Typography>
        </Box>

        <Box sx={{ my: 4 }}>
          <Typography variant="h5" gutterBottom>
            Maintaining the Quality of your Personal Information
          </Typography>
          <Typography paragraph>
            It is an important to us that your Personal Information is up to date. We will take
            reasonable steps to make sure that your Personal Information is accurate, complete and
            up-to-date. If you find that the information we have is not up to date or is inaccurate,
            please advise us as soon as practicable so we can update our records and ensure we can
            continue to provide quality services to you.
          </Typography>
        </Box>

        <Box sx={{ my: 4 }}>
          <Typography variant="h5" gutterBottom>
            Policy Updates
          </Typography>
          <Typography paragraph>
            This Policy may change from time to time. Where appropriate, we will notify you of
            changes to this Policy.
          </Typography>
        </Box>

        <Box sx={{ my: 4 }}>
          <Typography variant="h5" gutterBottom>
            Privacy Policy Complaints and Enquiries
          </Typography>
          <Typography paragraph>
            If you have any queries or complaints about our Privacy Policy please contact us at:
          </Typography>
          <Typography paragraph>
            <strong>Email:</strong> support@bidlogiq.ai
          </Typography>
          <Typography paragraph>
            <strong>Phone:</strong>+61 (0)7 2143 6035
          </Typography>
        </Box>
      </Paper>
    </PageContainer>
  );
};

export default PrivacyPolicyPage;
